import { apiClient } from './client'

// Onboarding API interfaces
export interface OnboardingStatus {
  needsPasswordSetup: boolean
  needsEmailVerification: boolean
  needsPinSetup: boolean
  isComplete: boolean
}

export interface OnboardingStatusResponse {
  onboardingStatus: OnboardingStatus
  nextStep: 'password_setup' | 'email_verification' | 'pin_setup' | 'complete'
}

export interface PasswordSetupRequest {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

export interface EmailVerificationRequest {
  verificationCode: string
}

export interface PinSetupRequest {
  pin: string
  confirmPin: string
}

// API Functions
export const onboardingApi = {
  // Get current onboarding status
  getStatus: async (): Promise<OnboardingStatusResponse> => {
    const response = await apiClient.get('/onboarding/status')
    return response.data.data
  },

  // Set up password (first time login)
  setupPassword: async (data: PasswordSetupRequest): Promise<{ nextStep: string; message: string }> => {
    const response = await apiClient.post('/onboarding/setup-password', data)
    return {
      nextStep: response.data.data.nextStep,
      message: response.data.message
    }
  },

  // Send email verification code
  sendEmailVerification: async (): Promise<{ expiresIn: number; message: string }> => {
    const response = await apiClient.post('/onboarding/send-verification')
    return {
      expiresIn: response.data.data.expiresIn,
      message: response.data.message
    }
  },

  // Verify email with code
  verifyEmail: async (data: EmailVerificationRequest): Promise<{ nextStep: string; message: string }> => {
    const response = await apiClient.post('/onboarding/verify-email', data)
    return {
      nextStep: response.data.data.nextStep,
      message: response.data.message
    }
  },

  // Set up PIN
  setupPin: async (data: PinSetupRequest): Promise<{ nextStep: string; onboardingComplete: boolean; message: string }> => {
    const response = await apiClient.post('/onboarding/setup-pin', data)
    return {
      nextStep: response.data.data.nextStep,
      onboardingComplete: response.data.data.onboardingComplete,
      message: response.data.message
    }
  },

  // Complete onboarding
  complete: async (): Promise<{ redirectTo: string; message: string }> => {
    const response = await apiClient.post('/onboarding/complete')
    return {
      redirectTo: response.data.data.redirectTo,
      message: response.data.message
    }
  }
}

export default onboardingApi